import { Component, EventEmitter, inject, OnDestroy, OnInit, Output } from '@angular/core';
import { NgIf } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { StateNotifyService } from '../../../../common/state/service/state-notify.service';
import { Subject, takeUntil } from 'rxjs';
import { TrailDocumentModification } from '../../dto/trail-document-modification';
import { TrailDocumentModificationService } from '../../service/trail-document-modification.service';
import { Trail } from '../../dto/trail';
import { TrailDocumentModificationDetailComponent } from '../trail-document-modification-detail/trail-document-modification-detail.component';

@Component({
  selector: 'dpi-trail-document-modification',
  imports: [MatCardModule, MatTableModule, MatIconModule, MatButtonModule, MatToolbarModule, MatDialogModule,
    MatTooltipModule, NgIf],
  templateUrl: './trail-document-modification.component.html',
  styleUrl: './trail-document-modification.component.scss'
})
export class TrailDocumentModificationComponent implements OnInit, OnDestroy {
  trail: Trail;
  displayedColumns: string[] = ['name', 'description', 'tested_text', 'trail_updated_text'];
  dataSource: TrailDocumentModification[] = [];
  destroyed$ = new Subject<void>(); // notifier of component destroyed
  @Output('listSizeChanged') listSize = new EventEmitter<number>();
  isReadonly: boolean;
  private _dialog = inject(MatDialog);

  constructor(private notifyService: StateNotifyService, private trailDocumentModificationService: TrailDocumentModificationService) {
    this.trail = this.notifyService.getSelectedTrail();
    this.isReadonly = true;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngOnInit(): void {
    this.notifyService.trailSelection$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(model => {
        this.trail = model;
        this.reload(model);
      });

    this.notifyService.readonlyMode$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(res => {
        this.isReadonly = res;
      });
  }

  reload(selectedTrail: Trail) {
    this.trailDocumentModificationService.getAllSetupOfTrial(selectedTrail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (res: any) => {
          this.dataSource = res;
          this.listSize.emit(this.dataSource.length);
          for (var obj of this.dataSource) {
            if (obj.tested) {
              obj.tested_text = 'Yes';
            } else {
              obj.tested_text = 'No';
            }
            if (obj.trail_updated) {
              obj.trail_updated_text = 'Yes';
            } else {
              obj.trail_updated_text = 'No';
            }
          }
        },
        error: (error: any) => {
          console.error('Error: ', error);
          this.dataSource = [];
        }
      })
  }

  selectTrailDocumentModification(selected: TrailDocumentModification) {
    if (!this.isReadonly) {
      var objCopy: TrailDocumentModification = { ...selected };
      const dialogRef = this._dialog.open(TrailDocumentModificationDetailComponent, { data: objCopy });
      dialogRef.afterClosed()
        .pipe(takeUntil(this.destroyed$))
        .subscribe(result => {
          if (result !== undefined) {
            if (dialogRef.componentInstance.isToUpdate) {
              this.update(dialogRef.componentInstance.trailDocumentModification);
            }
          } else if (dialogRef.componentInstance.isToDelete) {
            this.delete(dialogRef.componentInstance.trailDocumentModification.id);
          }
        });
    }
  }

  //--- CRUD actions ---

  create() {
    this.trailDocumentModificationService.createDocumentModification(this.trail.id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: model => {
          this.addNewModelToDataSource(model);
          this.selectTrailDocumentModification(model);
        },
        error: err => { console.log(err); }
      });
  }

  private addNewModelToDataSource(model: TrailDocumentModification) {
    let list: TrailDocumentModification[] = [];
    for (var item of this.dataSource) {
      list.push(item);
    }
    list.push(model);
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  update(model: TrailDocumentModification) {
    this.trailDocumentModificationService.updateDocumentModification(model)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.replaceModelInDataSource(res);
        },
        error: err => { console.log(err); }
      });
  }

  private replaceModelInDataSource(model: TrailDocumentModification) {
    let list: TrailDocumentModification[] = [];
    for (var item of this.dataSource) {
      if (item.id === model.id) {
        model.tested_text = model.tested? 'Yes':'No';
        model.trail_updated_text = model.trail_updated? 'Yes':'No';
        list.push(model);
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }

  delete(modelId: number) {
    this.trailDocumentModificationService.deleteDocumentModification(modelId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: res => {
          this.removeModelFromDataSource(modelId);
        },
        error: err => { console.log(err); }
      });
  }

  private removeModelFromDataSource(modelId: number) {
    let list: TrailDocumentModification[] = [];
    for (var item of this.dataSource) {
      if (item.id === modelId) {
        //the model is to be removed --> do nothing
      } else {
        list.push(item);
      }
    }
    this.dataSource = list;
    this.listSize.emit(this.dataSource.length);
  }


}
